import React, { useContext, useState, useEffect, useRef } from "react";
import "../styles/Chatbot.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import defaultProfile from "../images/default-profile.png";
import { UrlContext } from "../contexts/UrlContext";

// Chat client for Ollama-compatible endpoint
class OllamaChatClient {
  constructor({ apiKey, baseUrl, model }) {
    this.apiKey = apiKey;
    this.baseUrl = "https://ai.expertpowerhouse.com/chat";
    this.model = model || "llama3.1"; // Default model; adjust as needed
  }

  async sendMessage(prompt, conversation = [], options = {}) {
    try {
      // For this backend, only a single query string is required
      const response = await fetch(this.baseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify({ query: prompt }),
      });

      if (!response.ok) {
        let message = response.statusText;
        try {
          const err = await response.json();
          message = err.error?.message || err.message || JSON.stringify(err);
        } catch (_) {
          // ignore JSON parse error
        }
        throw new Error(`Chat API Error: ${message}`);
      }

      const data = await response.json();

      // Parse response according to provided backend example
      const content = Array.isArray(data?.responses)
        ? data.responses.join("\n")
        : data?.message?.content ||
          data?.content ||
          data?.choices?.[0]?.message?.content ||
          data?.response ||
          (typeof data === "string" ? data : JSON.stringify(data));

      return {
        id: data.id || null,
        content,
        finish_reason: data.stop_reason || data.finish_reason || null,
        model: data.model || options.model || this.model,
      };
    } catch (error) {
      console.error("Error in OllamaChatClient:", error);
      throw error;
    }
  }

  _formatConversation(conversation, newPrompt) {
    const messages = [];

    // Add previous conversation
    conversation.forEach((msg) => {
      messages.push({
        role: msg.role === "bot" ? "assistant" : "user",
        content: msg.content,
      });
    });

    // Add new user message
    if (newPrompt) {
      messages.push({
        role: "user",
        content: newPrompt,
      });
    }

    return messages;
  }
}

const Chatbot = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const { API_URL, URI } = useContext(UrlContext);

  // State to control chatbot position
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Reference to the chatbot component for drag and drop
  const chatbotRef = useRef(null);

  // References for MCP and message scrolling
  const mcpRef = useRef(null);
  const messagesEndRef = useRef(null);

  // API key and initialization for Ollama-compatible chat service
  const OLLAMA_API_KEY = "1234"; // TODO: move to environment/config for production

  useEffect(() => {
    mcpRef.current = new OllamaChatClient({
      apiKey: OLLAMA_API_KEY,
      // Do not pass baseUrl so it uses "/chat" in development (proxy) and full URL in production
      model: "llama3.1", // adjust if your VM runs a different model name
    });
  }, []);

  // Auto-scroll to the last message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const toggleChatbot = () => {
    setIsVisible(!isVisible);
    if (!isVisible && messages.length === 0) {
      // Welcome message when opening the chatbot
      setMessages([
        {
          role: "bot",
          content:
            "Hello, I'm your virtual assistant. How can I help you today?",
        },
      ]);
    }
  };

  const handleUserResponse = async () => {
    const input = userInput.trim();
    if (!input) return;

    // Add user message
    setMessages((prev) => [...prev, { role: "user", content: input }]);
    setUserInput("");
    setLoading(true);

    try {
      // Send message to the model using MCP
      const response = await mcpRef.current.sendMessage(input, messages);

      // Add model response
      setMessages((prev) => [
        ...prev,
        { role: "bot", content: response.content },
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(
        "There was an error processing your message. Please try again."
      );

      // Add error message to chat
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          content:
            "Sorry, there was an error processing your message. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key to send message
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleUserResponse();
    }
  };

  // Functions to handle chatbot dragging
  const handleMouseDown = (e) => {
    // Only allow dragging from the header
    if (e.target.closest(".chatbot-header")) {
      setIsDragging(true);
      const chatbotRect = chatbotRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - chatbotRect.left,
        y: e.clientY - chatbotRect.top,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      // Calculate new position
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Set limits to keep the chatbot within the screen
      const chatbotRect = chatbotRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - chatbotRect.width;
      const maxY = window.innerHeight - chatbotRect.height;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove event listeners for drag
  useEffect(() => {
    if (isVisible) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("mousedown", handleMouseDown);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isVisible, isDragging]);

  // Render formatted code
  const renderMessage = (content) => {
    // Split content by code blocks
    const parts = content.split(/(```[\s\S]*?```)/g);

    return parts.map((part, index) => {
      // If it's a code block
      if (part.startsWith("```") && part.endsWith("```")) {
        // Extract language and code
        const match = part.match(/```(\w*)\n([\s\S]*?)```/);
        const language = match?.[1] || "";
        const code = match?.[2] || part.slice(3, -3);

        return (
          <pre key={index} className="code-block">
            {language && <div className="code-language">{language}</div>}
            <code>{code}</code>
          </pre>
        );
      }

      // Normal paragraphs with line breaks
      return (
        <div key={index} className="text-content">
          {part.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      );
    });
  };

  return (
    <>
      <ToastContainer />
      {!isVisible && (
        <button className="chatbot-toggle" onClick={toggleChatbot}>
          💬
        </button>
      )}
      {isVisible && (
        <div className="chatbot-overlay">
          <div
            className={`chatbot ${showResultsPanel ? "shift-right" : ""}`}
            ref={chatbotRef}
            style={{
              transform: `translate(${position.x}px, ${position.y}px)`,
              cursor: isDragging ? "grabbing" : "auto",
            }}
          >
            <div className="chatbot-header">
              <h3>EPH Assistant</h3>
              <button className="chatbot-close" onClick={toggleChatbot}>
                ✖
              </button>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`chatbot-message ${
                    msg.role === "user" ? "user-message" : "bot-message"
                  }`}
                >
                  {renderMessage(msg.content)}
                </div>
              ))}
              {loading && (
                <div className="chatbot-message bot-message">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="chatbot-input">
              <textarea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message... (Shift+Enter for new lines)"
                disabled={loading}
                rows={1}
              />
              <button
                className="ask-button"
                onClick={handleUserResponse}
                disabled={loading}
              >
                {loading ? "Thinking..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
